<template>
  <div ref="root">
    <div
      ref="wrapper"
      class="qtm-table-wrapper"
      :class="{ 'overflow-x-auto': !noScroll, 'qtm-scroll-disabled': disableScroll }"
      tabindex="-1"
      @scroll="onScroll"
    >
      <table
        class="qtm-table text-dark-grey"
        :class="[tableClass, { 'qtm-table--no-vertical-borders': noVerticalBorders }]"
      >
        <thead
          v-if="!noHeader"
          ref="theadRef"
          class="qtm-table-header text-secondary"
          :class="{ 'qtm-h4': !dense, 'qtm-h4-small':dense, 'text-no-wrap': !wrap }"
        >
          <slot name="header-prepend" />
          <tr>
            <th
              v-for="(header, i) in headers"
              :key="header.value"
              :class="header.headerClass || header.cellClass"
              :colspan="header.colspan"
              :style="cellStyles[i]"
            >
              <slot :name="`header.${header.value}`" :header="header">
                {{ header.title }}
              </slot>
            </th>
          </tr>
        </thead>
        <slot v-if="!headerOnly" :cell-styles="cellStyles">
          <template v-for="(item, index) in items" :key="item[itemValue]">
            <tr
              :class="{
                'qtm-table-active': activeValues.includes(item.id),
                'cursor-pointer': hover,
                'qtm-table-row--hover-interactive': hover && hoverColor === 'interactive',
              }"
              @click="hover && $emit('click:row', $event, { item })"
            >
              <slot
                v-for="(header, i) in headers"
                :key="header.value"
                :cell-style="cellStyles[i]"
                :item="item"
                :name="`item-cell.${header.value}`"
              >
                <td
                  :key="header.value"
                  :class="header.cellClass"
                  :style="cellStyles[i]"
                >
                  <slot :name="`item.${header.value}`" :index="index" :item="item">
                    {{ item[header.value] }}
                  </slot>
                </td>
              </slot>
            </tr>
            <slot name="row-append" :item="item" />
          </template>
        </slot>
        <slot name="items-append" />
      </table>
    </div>
    <slot v-if="!items.length && !headerOnly" name="empty">
      <qtm-skeleton v-if="loading" />
      <div v-else-if="noItemsText" class="text-center py-4" v-text="noItemsText" />
    </slot>
    <slot name="append" />
  </div>
</template>

<script setup lang="ts">
export interface Header {
  title: string
  value: string
  colspan?: number
  width?: number
  cellClass?: string
  minWidth?: number
  headerClass?: string
  shrink?: boolean
  tooltipText?: string
}

export interface Props {
  activeValues?: number[]
  borderColor?: string
  comfortable?: boolean
  dense?: boolean
  disableScroll?: boolean
  fixedColumns?: '0' | '1' | '2' | 0 | 1 | 2
  headerBackground?: string
  headerOnly?: boolean
  hover?: boolean
  hoverColor?: 'grey' | 'interactive'
  headers: Header[]
  items?: any[]
  itemValue?: string
  loading?: boolean
  noHeader?: boolean
  noItemsText?: string
  noScroll?: boolean
  noVerticalBorders?: boolean
  scrollSync?: ComponentPublicInstance,
  wrap?: boolean
}

defineEmits(['click:row'])

const props = withDefaults(defineProps<Props>(), {
  activeValues: () => [],
  borderColor: 'light-grey',
  fixedColumns: 1,
  hoverColor: 'grey',
  headerBackground: 'rgb(var(--v-theme-background))',
  items: () => [],
  itemValue: 'id',
  noItemsText: 'No items',
  noScroll: false,
  scrollSync: undefined,
})

const borderVar = computed(() => `rgb(var(--v-theme-${props.borderColor}))`)

const theadRef = useTemplateRef<HTMLElement>('theadRef')
const measuredColumnWidths = ref<number[]>([])

const measureColumnWidths = () => {
  if (!theadRef.value) return
  const ths = Array.from(theadRef.value.querySelectorAll('tr:first-child > th'))
  measuredColumnWidths.value = ths.map(th => (th as HTMLElement).offsetWidth)
}

onMounted(() => {
  measureColumnWidths()
  const observer = new ResizeObserver(measureColumnWidths)
  const ths = theadRef.value?.querySelectorAll('tr:first-child > th') ?? []
  ths.forEach(th => observer.observe(th))
  onUnmounted(() => observer.disconnect())
})

const leftOffsets = computed(() => {
  const offsets = []
  let offset = 0
  let i = 0

  while (i < Number(props.fixedColumns)) {
    offsets.push(`${offset}px`)
    const measured = measuredColumnWidths.value[i]
    offset += measured || props.headers[i]?.width || 0
    i += 1
  }

  return offsets
})

const cellStyles = computed(() => props.headers.map((header, i) => {
  const styles = []
  const offset = leftOffsets.value[i]

  if (offset !== undefined) {
    styles.push(`left: ${offset}`)
  }

  if (header.width) {
    ['max-', 'min-', ''].forEach(prefix => {
      styles.push(`${prefix}width: ${header.width}px`)
    })
  }
  else if (header.minWidth) {
    styles.push(`min-width: ${header.minWidth}px`)
  }
  else if (header.shrink) {
    styles.push('width: 1%')
  }

  return styles.join(';')
}))

const wrapperEl = useTemplateRef<HTMLElement>('wrapper')
const rootEl = useTemplateRef<HTMLElement>('root')

const scrolled = ref(false)
const fixedClass = computed(() => `qtm-table-fixed-${props.fixedColumns}`)

let syncObserver: IntersectionObserver | null = null

onMounted(() => {
  if (!props.scrollSync || !rootEl.value) return

  syncObserver = new IntersectionObserver((entries) => {
    if (entries[0]?.isIntersecting && wrapperEl.value) {
      const syncWrapper = props.scrollSync!.$el?.querySelector('.qtm-table-wrapper') ?? props.scrollSync!.$el
      if (syncWrapper) {
        wrapperEl.value.scrollTo({ left: syncWrapper.scrollLeft, behavior: 'instant' })
      }
    }
  }, { threshold: 0 })

  syncObserver.observe(rootEl.value)
})

onBeforeUnmount(() => syncObserver?.disconnect())

const onScroll = (event: any) => {
  scrolled.value = Number(event?.target?.scrollLeft) > 0

  if (props.scrollSync) {
    const scrollElement = props.scrollSync.$el?.querySelector('.qtm-table-wrapper') || props.scrollSync.$el
    if (scrollElement) {
      scrollElement.scrollTo({
        left: event.target.scrollLeft,
        behavior: 'instant'
      })
    }
  }
}

const tableClass = computed(() => ({
  'qtm-table--comfortable': props.comfortable,
  'dense': props.dense,
  'qtm-table-scrolled': scrolled.value,
  [fixedClass.value]: Number(props.fixedColumns) > 0,
  'qtm-body': !props.dense,
  'qtm-body-small': props.dense
}))
</script>

<style lang="scss">
.qtm-table-wrapper {
  scrollbar-width: auto;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &.qtm-scroll-disabled {
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}

.qtm-table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  text-align: left;

  td,th {
    background-color: white;
    border: 1px solid v-bind(borderVar);
    line-height: 1.3;
    text-overflow: ellipsis;
    vertical-align: top;
    overflow: hidden;
  }

  td {
    padding: 16px 8px;
  }

  th {
    padding: 8px;
    vertical-align: middle;
  }

  .qtm-table-active td {
    background-color: rgb(var(--v-theme-interactive-lighten-1));
  }

  tr.cursor-pointer:hover td {
    background-color: rgba(var(--v-border-color), var(--v-hover-opacity));
  }

  tr.cursor-pointer.qtm-table-row--hover-interactive:hover td {
    background-color: rgb(var(--v-theme-interactive-lighten-1));
  }

  &.dense td {
    padding: 12px 8px;
  }

  &.qtm-table--comfortable td,
  &.qtm-table--comfortable th {
    padding: 16px;
  }

  &.qtm-table--no-vertical-borders td,
  &.qtm-table--no-vertical-borders th {
    border-left: none;
    border-right: none;
    border-top: none;
  }

  &.qtm-table--no-vertical-borders > tr:last-of-type td,
  &.qtm-table--no-vertical-borders > tr:last-of-type th {
    border-bottom: none;
  }
}

.qtm-table-header > tr > th {
  background-color: v-bind(headerBackground);
  font-weight: 500;
}

$columns: 1, 2;

@each $column in $columns {
  .qtm-table-scrolled.qtm-table-fixed-#{$column} {
    tr > th:nth-child(-n + #{$column}), tr > td:nth-child(-n + #{$column}) {
      position: sticky;
      left: 0;
      z-index: 1;
    }

    tr > th:nth-child(#{$column}), tr > td:nth-child(#{$column}) {
      box-shadow: 5px 0 5px -2px rgba(1.0, 1.0, 1.0, 0.2);
    }
  }
}
</style>
